/*
 * CellFilterSet Dashboard
 * 
 * This dashboard allows viewing and manually verifying CellFilterSets created for different users.
 * It displays detailed cell information including metadata that helps verify the quality of the filter sets.
 * 
 * Current Implementation:
 * - Uses existing `retrieveCellFilterSet` and `getPaginatedCellFromCellFilterSet` functions
 * - Uses the new batch API endpoint `POST /api/cell_detailed_info/` for efficient data fetching
 * - Provides real-time data for all metadata fields including quality flags and annotation history
 * 
 * API Integration:
 * - POST /api/cell_detailed_info/ - Batch endpoint that returns detailed cell metadata
 *   Request: { "cell_ids": [1, 2, 3], "annotator_id": 123 } (snake_case)
 *   Response: Array of DetailedCellInfo objects with all metadata (auto-converted to camelCase)
 * 
 * Features:
 * - Efficient batch loading: Fetches multiple cells in a single request
 * - Real metadata: All quality flags and annotation history from backend
 * - Pagination support: Only loads data for the current page
 * - Performance optimized: Reduced API calls and faster loading times
 * 
 * Future Enhancements:
 *    - Add data export functionality (CSV, Excel)
 *    - Add filtering and sorting capabilities
 *    - Add search functionality
 *    - Add cell detail view with images
 *    - Add bulk actions for marking cells as reviewed
 *    - Implement caching for frequently accessed data
 */

import React, { Component, useContext, useEffect, useState } from 'react';
import config from './../config';
import { auth_fetch } from './../utils/ajax';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardBody,
    CardHeader,
    FormSelect,
    Badge,
    ButtonGroup,
} from 'shards-react';
import PageTitle from './../components/common/PageTitle';
import { Cell, Dataset, Wsi, User, Consensus } from './../models/BackendModels';
import { UserContext } from './../context/UserContext';
import { checkSuccess } from './../utils/ajax';
import ClipLoader from 'react-spinners/ClipLoader';
import styled from 'styled-components';

type CellId = number;
type UserId = number;

// Extended interface for detailed cell information (camelCase for auto converter)
interface DetailedCellInfo {
    cellId: number;
    segmentationSetId: number;
    wsiId: number;
    isBadWsi: boolean;
    datasetId: number;
    datasetName: string;
    isBadDataset: boolean;
    isHoneypotCell: boolean;
    isPriorityQueue: boolean;
    isAlreadyLabeledByUser: boolean;
    labeledByAnnotatorIds?: number[] | null; // Make optional to handle API variations
    consensusLabel?: string | null; // New field
    modelSchedulerPosition?: number | null; // New field
    isNewDataset?: boolean; // New field
    isLikelyRandomlySelected?: boolean; // New field
}

interface Option {
    id: number | null;
    name: string;
}

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    
    th, td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
    }
    
    th {
        background-color: #f2f2f2;
        font-weight: bold;
        position: sticky;
        top: 0;
        z-index: 1;
    }
    
    tr:nth-child(even) {
        background-color: #f9f9f9;
    }
    
    tr:hover {
        background-color: #f5f5f5;
    }
`;

const ScrollableContainer = styled.div`
    max-height: 600px;
    overflow-y: auto;
    border: 1px solid #ddd;
`;

// Available annotator options (from existing code)
const VALID_ANNOTATOR_OPTIONS = [
    {
        id: 49,
        name: 'Karate Kangaroo',
    },
    {
        id: 50,
        name: 'Pouncing Panther',
    },
    {
        id: 51,
        name: 'Bumbling Bee',
    },
    {
        id: 52,
        name: 'Fluttering Finch',
    },
    {
        id: 53,
        name: 'Whispering Willow',
    },
    {
        id: 54,
        name: 'Darting Dragonfly',
    },
    {
        id: 56,
        name: 'Annotateur 007',
    },
    {
        id: 57,
        name: 'Silvery Seahorse',
    },
    {
        id: 58,
        name: 'Swift Swallowtail',
    },
    {
        id: 59,
        name: 'Noble Nightingale',
    },
    {
        id: 60,
        name: 'BlairBrook',
    },
    {
        id: 61,
        name: 'Jan Eckardt labelset_migration_1_and_2_to_3',
    },
    {
        id: 62,
        name: 'Uwe Marr labelset_migration_1_and_2_to_3',
    },
    {
        id: 68,
        name: 'SapphireNomad',
    },
    {
        id: 70,
        name: 'CoralCrest',
    },
    {
        id: 71,
        name: 'LunarLark',
    },
    {
        id: 72,
        name: 'MLL_BM_Cytomorphology',
    },
    {
        id: 73,
        name: 'SilverSprout',
    },   
    {
        id: 74,
        name: 'PebblePeak',
    },
];

// Available cell filter set options
const CELL_FILTER_SET_OPTIONS = [
    // { id: 1, name: 'CellFilterSet 1' },
    // { id: 2, name: 'CellFilterSet 2' },
    // { id: 3, name: 'CellFilterSet 3' },
    { id: 4, name: 'CellFilterSet 4' },
    // { id: 5, name: 'CellFilterSet 5' },
];

// Import existing functions
export const retrieveCellFilterSet = (
    cellFilterSetId: number,
    annotatorPriority: (number | null)[],
    userId: number | null = null,
): Promise<CellId[]> =>
    auth_fetch(`${config.backend}/api/cell_filterset/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            cellFilterSetId: cellFilterSetId,
            annotatorPriority: annotatorPriority,
            tailoredForAnnotator: userId,
        }),
    }).then(checkSuccess);

export const getPaginatedCellFromCellFilterSet = (
    cellFilterSetId: number,
    annotatorPriority: (number | null)[],
    userId: number | null = null,
    cellIdx: number,
): Promise<CellId[]> =>
    auth_fetch(`${config.backend}/api/cell_filterset/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            cellFilterSetId: cellFilterSetId,
            annotatorPriority: annotatorPriority,
            tailoredForAnnotator: userId,
            cellIdx: cellIdx,
        }),
    }).then(checkSuccess);

// Note: The individual fetch functions (fetchCell, fetchWsi, fetchDataset) are no longer needed
// since the new API endpoint provides all required data in a single batch request

// Function to fetch detailed cell information using the real API endpoint
const fetchDetailedCellInfo = async (cellIds: number[], annotatorId: number): Promise<DetailedCellInfo[]> => {
    try {
        const response = await auth_fetch(`${config.backend}/api/cell_detailed_info/`, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                cell_ids: cellIds,
                annotator_id: annotatorId,
            }),
        });
        
        const data = await checkSuccess(response);
        
        // Validate that the response is an array
        if (!Array.isArray(data)) {
            console.error('API response is not an array:', data);
            throw new Error('Invalid API response format: expected array');
        }
        
        return data;
    } catch (error) {
        console.error(`Error fetching detailed info for cells ${cellIds}:`, error);
        throw error;
    }
};

const CellFilterSetDashboard: React.FC = () => {
    const { user } = useContext(UserContext);
    const [selectedCellFilterSetId, setSelectedCellFilterSetId] = useState<number>(4);
    const [selectedUserId, setSelectedUserId] = useState<number>(49);
    const [cellIds, setCellIds] = useState<CellId[]>([]);
    const [detailedCellData, setDetailedCellData] = useState<DetailedCellInfo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [itemsPerPage, setItemsPerPage] = useState<number>(1000);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [skipAlreadyLabeled, setSkipAlreadyLabeled] = useState<boolean>(false);
    const [goToPageInput, setGoToPageInput] = useState<string>('');

    const fetchCellFilterSetData = async () => {
        if (!selectedCellFilterSetId || !selectedUserId) return;
        
        setLoading(true);
        setError(null);
        setDetailedCellData([]);
        setCurrentPage(1); // Reset to first page when fetching new data
        
        try {
            // Fetch cell IDs from the filter set
            const cellIdList = await retrieveCellFilterSet(
                selectedCellFilterSetId,
                [],
                selectedUserId
            );
            
            setCellIds(cellIdList);
            
            // Calculate pagination for the first page
            const startIndex = 0;
            const endIndex = Math.min(itemsPerPage, cellIdList.length);
            const currentPageCellIds = cellIdList.slice(startIndex, endIndex);
            
            // Fetch detailed information for current page cells in a single batch request
            const detailedInfo = await fetchDetailedCellInfo(currentPageCellIds, selectedUserId);
            setDetailedCellData(detailedInfo);
            
        } catch (err) {
            setError(`Failed to fetch cell filter set data: ${err}`);
            console.error('Error fetching cell filter set data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedCellFilterSetId && selectedUserId) {
            fetchCellFilterSetData();
        }
    }, [selectedCellFilterSetId, selectedUserId]);

    // Handle items per page change
    useEffect(() => {
        if (cellIds.length > 0) {
            setCurrentPage(1);
            handlePageChange(1);
        }
    }, [itemsPerPage]);

    // Handle skip already labeled change
    useEffect(() => {
        if (cellIds.length > 0) {
            setCurrentPage(1);
            handlePageChange(1);
        }
    }, [itemsPerPage, skipAlreadyLabeled]);

    // Filter the detailed cell data based on skipAlreadyLabeled setting
    const filteredDetailedCellData = skipAlreadyLabeled 
        ? detailedCellData.filter(cell => !cell.isAlreadyLabeledByUser)
        : detailedCellData;

    const renderBooleanBadge = (value: boolean | undefined | null) => (
        <Badge theme={value ? "warning" : "success"}>
            {value ? "Yes" : "No"}
        </Badge>
    );

    const renderAnnotatorIds = (ids: number[] | undefined | null) => {
        if (!ids || ids.length === 0) return "None";
        return ids.join(", ");
    };

    // Pagination calculations - need to work with filtered data
    const getFilteredCellIds = () => {
        if (!skipAlreadyLabeled) return cellIds;
        // We need to filter at the cell ID level, but we don't have the labeled status for all cells
        // For now, we'll use the current approach and filter after fetching
        return cellIds;
    };

    const filteredCellIds = getFilteredCellIds();
    const totalPages = Math.ceil(filteredCellIds.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredCellIds.length);

    // Function to handle page changes
    const handlePageChange = async (page: number) => {
        if (page < 1 || page > totalPages || page === currentPage) return;
        
        setCurrentPage(page);
        setLoading(true);
        setError(null);
        
        try {
            // Calculate which cells to fetch for this page
            const pageStartIndex = (page - 1) * itemsPerPage;
            const pageEndIndex = Math.min(pageStartIndex + itemsPerPage, filteredCellIds.length);
            const pageCellIds = filteredCellIds.slice(pageStartIndex, pageEndIndex);
            
            // Fetch detailed information for current page cells in a single batch request
            const detailedInfo = await fetchDetailedCellInfo(pageCellIds, selectedUserId);
            setDetailedCellData(detailedInfo);
            
        } catch (err) {
            setError(`Failed to fetch page data: ${err}`);
            console.error('Error fetching page data:', err);
        } finally {
            setLoading(false);
        }
    };

    // Function to handle GoTo page functionality
    const handleGoToPage = () => {
        const pageNumber = parseInt(goToPageInput);
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
            alert(`Please enter a valid page number between 1 and ${totalPages}`);
            return;
        }
        handlePageChange(pageNumber);
        setGoToPageInput(''); // Clear the input after successful navigation
    };

    // Function to handle Enter key press in GoTo page input
    const handleGoToPageKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleGoToPage();
        }
    };

    // Function to render pagination controls
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const maxVisiblePages = 5;
        const startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        const buttons = [];
        
        // Previous button
        buttons.push(
            <Button
                key="prev"
                size="sm"
                outline
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="mx-1"
            >
                ‹ Prev
            </Button>
        );

        // First page if not visible in range
        if (startPage > 1) {
            buttons.push(
                <Button
                    key={1}
                    size="sm"
                    theme={1 === currentPage ? "primary" : "light"}
                    onClick={() => handlePageChange(1)}
                    className="mx-1"
                >
                    1
                </Button>
            );
            if (startPage > 2) {
                buttons.push(
                    <Button
                        key="ellipsis1"
                        size="sm"
                        theme="light"
                        disabled
                        className="mx-1"
                    >
                        ...
                    </Button>
                );
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            buttons.push(
                <Button
                    key={i}
                    size="sm"
                    theme={i === currentPage ? "primary" : "light"}
                    onClick={() => handlePageChange(i)}
                    className="mx-1"
                >
                    {i}
                </Button>
            );
        }

        // Last page if not visible in range
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                buttons.push(
                    <Button
                        key="ellipsis2"
                        size="sm"
                        theme="light"
                        disabled
                        className="mx-1"
                    >
                        ...
                    </Button>
                );
            }
            buttons.push(
                <Button
                    key={totalPages}
                    size="sm"
                    theme={totalPages === currentPage ? "primary" : "light"}
                    onClick={() => handlePageChange(totalPages)}
                    className="mx-1"
                >
                    {totalPages}
                </Button>
            );
        }

        // Next button
        buttons.push(
            <Button
                key="next"
                size="sm"
                outline
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="mx-1"
            >
                Next ›
            </Button>
        );

        return (
            <div className="d-flex flex-column align-items-center">
                <div className="d-flex justify-content-center align-items-center mb-2">
                    {buttons}
                </div>
                <div className="d-flex align-items-center">
                    <span className="mr-2">Go to page:</span>
                    <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPageInput}
                        onChange={(e) => setGoToPageInput(e.target.value)}
                        onKeyPress={handleGoToPageKeyPress}
                        placeholder={`1-${totalPages}`}
                        className="form-control mr-2"
                        style={{ width: '80px', fontSize: '0.875rem' }}
                    />
                    <Button
                        size="sm"
                        theme="secondary"
                        onClick={handleGoToPage}
                        disabled={!goToPageInput.trim()}
                    >
                        Go
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <Container fluid className="main-content-container px-4" style={{ marginTop: '12vh' }}>
            <Row>
                <Col>
                    <PageTitle
                        title="CellFilterSet Dashboard"
                        subtitle="View and manually verify CellFilterSets for different users"
                        className="text-sm-left"
                    />
                </Col>
            </Row>

            <Row>
                <Col lg="12" md="12" sm="12" className="mb-4">
                    <Card small>
                        <CardHeader className="border-bottom">
                            <h6 className="m-0">Filter Configuration</h6>
                        </CardHeader>
                        <CardBody>
                            <Row>
                                <Col md="3">
                                    <label htmlFor="cellFilterSetSelect">Cell Filter Set:</label>
                                    <FormSelect
                                        id="cellFilterSetSelect"
                                        value={selectedCellFilterSetId}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedCellFilterSetId(Number(e.target.value))}
                                    >
                                        {CELL_FILTER_SET_OPTIONS.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </FormSelect>
                                </Col>
                                <Col md="3">
                                    <label htmlFor="userSelect">User:</label>
                                    <FormSelect
                                        id="userSelect"
                                        value={selectedUserId}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedUserId(Number(e.target.value))}
                                    >
                                        {VALID_ANNOTATOR_OPTIONS.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.id} - {option.name}
                                            </option>
                                        ))}
                                    </FormSelect>
                                </Col>
                                <Col md="2">
                                    <label htmlFor="itemsPerPageSelect">Items per Page:</label>
                                    <FormSelect
                                        id="itemsPerPageSelect"
                                        value={itemsPerPage}
                                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setItemsPerPage(Number(e.target.value))}
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={200}>200</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                    </FormSelect>
                                </Col>
                                <Col md="2">
                                    <div className="form-check mt-4">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="skipAlreadyLabeledCheck"
                                            checked={skipAlreadyLabeled}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSkipAlreadyLabeled(e.target.checked)}
                                        />
                                        <label className="form-check-label" htmlFor="skipAlreadyLabeledCheck">
                                            Hide already labeled
                                        </label>
                                    </div>
                                </Col>
                                <Col md="2" className="d-flex align-items-end">
                                    <Button 
                                        theme="primary" 
                                        onClick={fetchCellFilterSetData}
                                        disabled={loading}
                                    >
                                        Refresh Data
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {cellIds.length > 0 && !loading && !error && (
                <Row>
                    <Col lg="12" md="12" sm="12" className="mb-4">
                        <Card small>
                            <CardHeader className="border-bottom">
                                <h6 className="m-0">Summary</h6>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Total Cells:</strong> {cellIds.length}
                                        {skipAlreadyLabeled && (
                                            <span className="text-muted ml-2">
                                                (Filtered: {filteredDetailedCellData.length} shown)
                                            </span>
                                        )}
                                    </div>
                                    <div>
                                        <strong>Items per Page:</strong> {itemsPerPage}
                                    </div>
                                    <div>
                                        <strong>Total Pages:</strong> {totalPages}
                                    </div>
                                    <div>
                                        <strong>Current Page:</strong> {currentPage}
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {loading && (
                <Row>
                    <Col className="text-center">
                        <ClipLoader loading={loading} size={50} />
                        <p>Loading cell filter set data...</p>
                    </Col>
                </Row>
            )}

            {error && (
                <Row>
                    <Col>
                        <Card small>
                            <CardBody>
                                <div className="alert alert-danger" role="alert">
                                    {error}
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {!loading && !error && detailedCellData.length > 0 && (
                <Row>
                    <Col>
                        <Card small>
                            <CardHeader className="border-bottom">
                                <h6 className="m-0">
                                    Cell Filter Set Results 
                                    <Badge theme="secondary" className="ml-2">
                                        Page {currentPage} of {totalPages} | Showing {startIndex + 1}-{endIndex} of {filteredCellIds.length} cells
                                        {skipAlreadyLabeled && (
                                            <span className="ml-1">(filtered)</span>
                                        )}
                                    </Badge>
                                </h6>
                            </CardHeader>
                            <CardBody className="p-0">
                                <ScrollableContainer>
                                    <StyledTable>
                                        <thead>
                                            <tr>
                                                <th>#</th> {/* New index column */}
                                                <th>Cell ID</th>
                                                <th>Segmentation Set ID</th>
                                                <th>WSI ID</th>
                                                <th>Is Bad WSI</th>
                                                <th>Dataset ID</th>
                                                <th>Dataset Name</th>
                                                <th>Is New Dataset</th>
                                                <th>Is Bad Dataset</th>
                                                <th>Is Honeypot Cell</th>
                                                <th>Is Priority Queue</th>
                                                <th>Already Labeled by User</th>
                                                <th>Labeled by Annotator IDs</th>
                                                <th>Consensus Label</th>
                                                <th>Model Scheduler Position</th>
                                                <th>Is (Likely) Randomly Selected</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredDetailedCellData.map((cellInfo, index) => (
                                                <tr key={cellInfo?.cellId || index}>
                                                    <td>{startIndex + index + 1}</td> {/* Global running index */}
                                                    <td>{cellInfo?.cellId || 'N/A'}</td>
                                                    <td>{cellInfo?.segmentationSetId || 'N/A'}</td>
                                                    <td>{cellInfo?.wsiId || 'N/A'}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isBadWsi)}</td>
                                                    <td>{cellInfo?.datasetId || 'N/A'}</td>
                                                    <td>{cellInfo?.datasetName || 'N/A'}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isNewDataset)}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isBadDataset)}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isHoneypotCell)}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isPriorityQueue)}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isAlreadyLabeledByUser)}</td>
                                                    <td>{renderAnnotatorIds(cellInfo?.labeledByAnnotatorIds)}</td>
                                                    <td>{cellInfo?.consensusLabel ?? 'N/A'}</td>
                                                    <td>{cellInfo?.modelSchedulerPosition ?? 'N/A'}</td>
                                                    <td>{renderBooleanBadge(cellInfo?.isLikelyRandomlySelected)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </StyledTable>
                                </ScrollableContainer>
                                {filteredDetailedCellData.length === 0 && skipAlreadyLabeled && (
                                    <div className="text-center p-4">
                                        <div className="alert alert-info" role="alert">
                                            All cells on this page have already been labeled by the user. Try the next page or disable the "Skip already labeled" filter.
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                            <CardBody className="border-top">
                                {renderPagination()}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}

            {!loading && !error && cellIds.length === 0 && (
                <Row>
                    <Col>
                        <Card small>
                            <CardBody>
                                <div className="alert alert-info" role="alert">
                                    No cells found for the selected filter set and user combination.
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default CellFilterSetDashboard; 